import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

const FREE_SHIPPING_PROGRESS_STYLES_ID = 'wavy-free-shipping-progress-styles';
const FREE_SHIPPING_SETTINGS_ID = 'wavy-free-shipping-settings';

/**
 * Injects the cart drawer free-shipping styles once per page.
 */
function ensureFreeShippingProgressStyles() {
  if (document.getElementById(FREE_SHIPPING_PROGRESS_STYLES_ID)) return;

  const style = document.createElement('style');
  style.id = FREE_SHIPPING_PROGRESS_STYLES_ID;
  style.textContent = `
    .cart-drawer__summary:has(.cart-free-shipping-progress:not([hidden])) {
      mask-image: none;
    }

    .cart-free-shipping-progress {
      width: 100%;
      display: grid;
      gap: 10px;
      color: var(--color-foreground);
    }

    .cart-free-shipping-progress[hidden] {
      display: none;
    }

    .cart-free-shipping-progress__message {
      margin: 0;
      font-family: var(--font-body--family);
      font-size: var(--font-size--sm);
      line-height: 1.4;
      text-align: center;
    }

    .cart-free-shipping-progress__message strong {
      font-weight: var(--font-weight-bold);
    }

    .cart-free-shipping-progress__track {
      position: relative;
      width: 100%;
      height: 8px;
      overflow: hidden;
      border-radius: 999px;
      background: rgb(var(--color-foreground-rgb) / 0.12);
    }

    .cart-free-shipping-progress__fill {
      display: block;
      width: 0;
      height: 100%;
      border-radius: inherit;
      background: var(--color-foreground);
      transition: width 350ms var(--animation-easing, ease);
      will-change: width;
    }

    .cart-free-shipping-progress.is-complete .cart-free-shipping-progress__fill {
      width: 100%;
    }

    @media (prefers-reduced-motion: reduce) {
      .cart-free-shipping-progress__fill {
        transition: none;
      }
    }
  `;

  document.head.appendChild(style);
}

/**
 * Formats a cart amount using the active storefront currency and locale.
 * @param {number} amountInCents
 * @param {string} currency
 * @returns {string}
 */
function formatCartMoney(amountInCents, currency) {
  const amount = Math.max(0, amountInCents) / 100;
  const hasFraction = amountInCents % 100 !== 0;

  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(hasFraction ? 2 : 0)}`;
  }
}

/**
 * Reads the free-shipping configuration rendered by the Theme Editor section.
 * @returns {{ enabled: boolean, country: string, currency: string, threshold: number | null } | null}
 */
function getFreeShippingConfig() {
  const settings = document.getElementById(FREE_SHIPPING_SETTINGS_ID);
  if (!(settings instanceof HTMLElement)) return null;

  const enabled = settings.dataset.enabled === 'true';
  const country = (settings.dataset.country || '').toUpperCase();
  const currency = settings.dataset.currency || window.Shopify?.currency?.active || 'USD';

  let threshold = null;
  if (country === 'US') threshold = Number(settings.dataset.usThreshold);
  if (country === 'CA') threshold = Number(settings.dataset.caThreshold);

  if (!Number.isFinite(threshold) || threshold <= 0) threshold = null;

  return {
    enabled,
    country,
    currency,
    threshold,
  };
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #freeShippingRequestController = null;

  /** @type {MutationObserver | null} */
  #drawerMutationObserver = null;

  /** @type {number | null} */
  #freeShippingUpdateFrame = null;

  /** @type {HTMLElement | null} */
  #freeShippingProgressElement = null;

  connectedCallback() {
    super.connectedCallback();
    ensureFreeShippingProgressStyles();
    this.#ensureFreeShippingProgressSkeleton();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleCartDiscountUpdate);
    document.addEventListener('shopify:section:load', this.#handleThemeEditorSectionLoad);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);

    this.#drawerMutationObserver = new MutationObserver(this.#handleDrawerMutation);
    this.#drawerMutationObserver.observe(this, { childList: true, subtree: true });

    this.#attachFreeShippingProgressSkeleton();
    this.#queueFreeShippingProgressUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#handleCartDiscountUpdate);
    document.removeEventListener('shopify:section:load', this.#handleThemeEditorSectionLoad);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);

    this.#freeShippingRequestController?.abort();
    this.#freeShippingRequestController = null;
    this.#drawerMutationObserver?.disconnect();
    this.#drawerMutationObserver = null;

    if (this.#freeShippingUpdateFrame !== null) {
      cancelAnimationFrame(this.#freeShippingUpdateFrame);
      this.#freeShippingUpdateFrame = null;
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // Keep the existing progress node and its current width visible while the
    // cart section morphs. The MutationObserver reattaches the same node before paint.
    this.#queueFreeShippingProgressUpdate();
  };

  #handleCartDiscountUpdate = () => {
    this.#queueFreeShippingProgressUpdate();
  };

  #handleThemeEditorSectionLoad = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.id === FREE_SHIPPING_SETTINGS_ID || event.target.querySelector(`#${FREE_SHIPPING_SETTINGS_ID}`)) {
      this.#attachFreeShippingProgressSkeleton();
      this.#queueFreeShippingProgressUpdate();
    }
  };

  #handleDialogOpen = () => {
    this.#attachFreeShippingProgressSkeleton();
    this.#updateStickyState();
    this.#queueFreeShippingProgressUpdate();
  };

  #handleDrawerMutation = () => {
    // Shopify morphs the cart drawer after quantity/add/remove operations. The
    // progress bar is intentionally a persistent client-side skeleton, so if the
    // morph removes it we immediately put the exact same DOM node back. Mutation
    // observer callbacks run before the next paint, preventing any visual flash.
    this.#attachFreeShippingProgressSkeleton();
  };

  #ensureFreeShippingProgressSkeleton() {
    if (this.#freeShippingProgressElement) return this.#freeShippingProgressElement;

    const progress = document.createElement('section');
    progress.className = 'cart-free-shipping-progress';
    progress.setAttribute('data-free-shipping-progress', '');
    progress.hidden = true;
    progress.innerHTML = `
      <p class="cart-free-shipping-progress__message" aria-live="polite"></p>
      <div
        class="cart-free-shipping-progress__track"
        role="progressbar"
        aria-label="Free shipping progress"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow="0"
      >
        <span class="cart-free-shipping-progress__fill"></span>
      </div>
    `;

    this.#freeShippingProgressElement = progress;
    return progress;
  }

  #attachFreeShippingProgressSkeleton() {
    const summary = this.querySelector('.cart-drawer__summary');
    const progress = this.#ensureFreeShippingProgressSkeleton();

    if (!summary) return;
    if (progress.parentElement === summary) return;

    summary.prepend(progress);
  }

  #queueFreeShippingProgressUpdate() {
    if (this.#freeShippingUpdateFrame !== null) {
      cancelAnimationFrame(this.#freeShippingUpdateFrame);
    }

    this.#freeShippingUpdateFrame = requestAnimationFrame(() => {
      this.#freeShippingUpdateFrame = null;
      this.#updateFreeShippingProgress();
    });
  }

  async #updateFreeShippingProgress() {
    const config = getFreeShippingConfig();
    const progress = this.#ensureFreeShippingProgressSkeleton();

    this.#attachFreeShippingProgressSkeleton();

    if (!config?.enabled || !config.threshold) {
      progress.hidden = true;
      return;
    }

    // Do not clear or reset the existing bar while the new cart total is being
    // fetched. Keeping the old width in place is what allows the next width to
    // animate smoothly instead of flashing back to zero.
    this.#freeShippingRequestController?.abort();
    const controller = new AbortController();
    this.#freeShippingRequestController = controller;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

      const cart = await response.json();
      if (controller.signal.aborted) return;

      if (cart.item_count === 0) {
        progress.hidden = true;
        return;
      }

      const currency = cart.currency || config.currency;
      const threshold = config.threshold;
      const cartTotal = Math.max(0, Number(cart.total_price) || 0);
      const remaining = Math.max(0, threshold - cartTotal);
      const progressPercent = Math.min(100, Math.max(0, (cartTotal / threshold) * 100));
      const roundedProgress = Math.round(progressPercent);
      const hasFreeShipping = remaining === 0;

      progress.hidden = false;
      progress.classList.toggle('is-complete', hasFreeShipping);
      progress.dataset.market = config.country;

      const message = progress.querySelector('.cart-free-shipping-progress__message');
      const track = progress.querySelector('.cart-free-shipping-progress__track');
      const fill = progress.querySelector('.cart-free-shipping-progress__fill');

      if (message) {
        message.innerHTML = hasFreeShipping
          ? '<strong>You have free shipping!</strong>'
          : `You're <strong>${formatCartMoney(remaining, currency)}</strong> away from free shipping.`;
      }

      if (track) {
        track.setAttribute('aria-valuenow', roundedProgress.toString());
        track.setAttribute(
          'aria-valuetext',
          hasFreeShipping
            ? 'Free shipping unlocked'
            : `${formatCartMoney(remaining, currency)} remaining for free shipping`
        );
      }

      if (fill instanceof HTMLElement) {
        // This is the only visual progress mutation during a cart update. Because
        // the fill node survives Shopify's section morph, CSS transitions animate
        // directly from the previous width to this new width.
        fill.style.width = `${progressPercent}%`;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.error('Failed to update free shipping progress:', error);
    } finally {
      if (this.#freeShippingRequestController === controller) {
        this.#freeShippingRequestController = null;
      }
    }
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
